
(() => {
  'use strict';

  /*
   * UNBEGUN Cart Controls 2.4
   *
   * - Linked add-ons
   * - Live cart line prices
   * - Header cart count
   * - Correct count after automatic reload
   */

  if (window.__unbegunCartControlsLoaded) return;
  window.__unbegunCartControlsLoaded = true;

  const ROOT = window.Shopify?.routes?.root || '/';
  const SERVICE_HANDLE = 'unbegun-personalizer';

  const LINK_KEYS = [
    '_Personalisatie koppeling',
    '_Cadeau koppeling',
    '_Toetsenbord koppeling',
    '_Maatwerk koppeling'
  ];

  const REOPEN_KEY = 'unbegun-open-cart-after-reload';
  const SAVED_COUNT_KEY = 'unbegun-cart-visible-count-after-update';

  let refreshPromise = null;
  let reconcilePromise = null;
  let cartActionRunning = false;
  let noteSaveTimer = null;

  let priceCart = null;
  let priceScheduled = false;
  let priceUpdating = false;

  let visibleCartCount = null;
  let countScheduled = false;

  const url = path =>
    `${ROOT}${path}`.replace(/\/{2,}/g, '/');

  const drawer = () =>
    document.querySelector('[data-quick-cart]');

  function isService(item) {
    return item?.handle === SERVICE_HANDLE ||
      item?.product_handle === SERVICE_HANDLE;
  }

  function quantity(value) {
    const number = Number(value);

    if (!Number.isFinite(number) || number < 0) {
      throw new Error('Ongeldig aantal.');
    }

    return Math.floor(number);
  }

  async function getCart() {
    const response = await fetch(
      `${url('cart.js')}?_=${Date.now()}`,
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }
    );

    if (!response.ok) {
      throw new Error('Winkelwagen ophalen mislukt.');
    }

    return response.json();
  }

  /*
   * CART COUNT
   */

  function calculateVisibleCount(cart) {
    return (cart.items || []).reduce(
      (total, item) =>
        total + (isService(item) ? 0 : Number(item.quantity || 0)),
      0
    );
  }

  function renderCount() {
    if (visibleCartCount === null) return;

    const expected = String(visibleCartCount);

    document.querySelectorAll('.js-cart-count').forEach(el => {
      if (el.textContent.trim() !== expected) {
        el.textContent = expected;
      }
    });
  }

  function scheduleCount() {
    if (countScheduled) return;

    countScheduled = true;

    requestAnimationFrame(() => {
      countScheduled = false;
      renderCount();
    });
  }

  function updateCount(cart) {
    visibleCartCount = calculateVisibleCount(cart);
    renderCount();
  }

  function rememberCount(cart) {
    const count = calculateVisibleCount(cart);

    sessionStorage.setItem(
      SAVED_COUNT_KEY,
      String(count)
    );

    visibleCartCount = count;
    renderCount();
  }

  function restoreCount() {
    const saved = sessionStorage.getItem(SAVED_COUNT_KEY);

    if (saved === null) return false;

    sessionStorage.removeItem(SAVED_COUNT_KEY);

    const count = Number(saved);

    if (!Number.isInteger(count) || count < 0) {
      return false;
    }

    visibleCartCount = count;
    renderCount();

    return true;
  }

  async function verifyCount() {
    for (const delay of [300, 900]) {
      await new Promise(resolve => setTimeout(resolve, delay));

      try {
        updateCount(await getCart());
      } catch (error) {
        console.warn('[UNBEGUN Cart] Count refresh', error);
      }
    }
  }

  function observeCount() {
    const observer = new MutationObserver(mutations => {
      const changed = mutations.some(mutation => {
        const node = mutation.target.nodeType === 3
          ? mutation.target.parentElement
          : mutation.target;

        if (node?.closest?.('.js-cart-count')) return true;

        return Array.from(mutation.addedNodes || []).some(added =>
          added.nodeType === 1 &&
          (
            added.matches?.('.js-cart-count') ||
            added.querySelector?.('.js-cart-count')
          )
        );
      });

      if (changed) scheduleCount();
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  /*
   * CART API
   */

  async function postUpdate(updates, note) {
    const payload = { updates };

    if (typeof note === 'string') {
      payload.note = note;
    }

    const response = await fetch(url('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description || 'Winkelwagen bijwerken mislukt.'
      );
    }

    updateCount(result);
    return result;
  }

  /*
   * LINKED SERVICES
   */

  function itemLinks(item) {
    return LINK_KEYS
      .filter(key => item?.properties?.[key])
      .map(key => ({
        key,
        value: String(item.properties[key])
      }));
  }

  function linksMatch(parent, service) {
    return itemLinks(parent).some(({ key, value }) =>
      String(service?.properties?.[key] || '') === value
    );
  }

  function parentFor(service, items) {
    return items.find(item =>
      !isService(item) && linksMatch(item, service)
    );
  }

  function buildUpdates(cart, requested = {}) {
    const updates = {};
    const desired = new Map();

    cart.items.forEach(item => {
      if (isService(item)) return;

      const next = Object.prototype.hasOwnProperty.call(
        requested,
        item.key
      )
        ? quantity(requested[item.key])
        : quantity(item.quantity);

      desired.set(item.key, next);

      if (next !== Number(item.quantity)) {
        updates[item.key] = next;
      }
    });

    cart.items.forEach(item => {
      if (!isService(item)) return;

      const parent = parentFor(item, cart.items);
      if (!parent) return;

      const next = desired.get(parent.key) ?? 0;

      if (Number(item.quantity) !== next) {
        updates[item.key] = next;
      }
    });

    return updates;
  }

  function cartIsValid(cart) {
    return cart.items.every(item => {
      if (!isService(item)) return true;

      const parent = parentFor(item, cart.items);

      return !parent ||
        Number(item.quantity) === Number(parent.quantity);
    });
  }

  async function synchronizeCart(requested = {}) {
    let cart = await getCart();

    const updates = buildUpdates(cart, requested);

    if (Object.keys(updates).length) {
      await postUpdate(updates);
    }

    cart = await getCart();

    if (!cartIsValid(cart)) {
      const repair = buildUpdates(cart);

      if (Object.keys(repair).length) {
        await postUpdate(repair);
        cart = await getCart();
      }
    }

    if (!cartIsValid(cart)) {
      throw new Error(
        'De aantallen van de gekoppelde add-ons komen niet overeen.'
      );
    }

    updateCount(cart);
    priceCart = cart;
    schedulePrices();

    return cart;
  }

  /*
   * MAIN CART FORM
   */

  function cartForm(target) {
    const form = target?.closest?.(
      '[data-unbegun-cart-form]'
    );

    if (!form?.querySelector(
      '.cart-template__item[data-item-key]'
    )) {
      return null;
    }

    return form;
  }

  function requestedQuantities(form) {
    const requested = {};

    form.querySelectorAll(
      '.cart-template__item[data-item-key]'
    ).forEach(row => {
      if (row.dataset.personalizationService === 'true') {
        return;
      }

      const input = row.querySelector(
        '[data-unbegun-quantity]'
      );

      if (input && row.dataset.itemKey) {
        requested[row.dataset.itemKey] = quantity(input.value);
      }
    });

    return requested;
  }

  function syncServiceInputs(form) {
    if (!form) return;

    const desired = new Map();
    const services = [];

    form.querySelectorAll(
      '.cart-template__item[data-item-key]'
    ).forEach(row => {
      const input = row.querySelector('input[name="updates[]"]');
      if (!input) return;

      const links = [
        row.dataset.personalizationLink,
        row.dataset.giftLink,
        row.dataset.keyboardLink,
        row.dataset.customLink
      ].filter(Boolean);

      if (row.dataset.personalizationService === 'true') {
        services.push({ input, links });
        return;
      }

      const amount = quantity(input.value);

      links.forEach(link => {
        desired.set(
          link,
          (desired.get(link) || 0) + amount
        );
      });
    });

    const processed = new Set();

    services.forEach(({ input, links }) => {
      const link = links.find(value => desired.has(value));
      if (!link) return;

      input.value = processed.has(link)
        ? '0'
        : String(desired.get(link));

      processed.add(link);
    });
  }

  async function updateCartPage(
    form,
    requested,
    checkout = false
  ) {
    if (cartActionRunning) return;

    cartActionRunning = true;

    const buttons = form.querySelectorAll('[type="submit"]');

    buttons.forEach(button => {
      button.disabled = true;
    });

    try {
      const updatedCart = await synchronizeCart(requested);

      /*
       * Version 2.4:
       * Save the correct parent-product count before reload.
       */
      rememberCount(updatedCart);

      if (checkout) {
        window.location.assign(url('checkout'));
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('[UNBEGUN Cart] Update failed', error);

      alert(
        'Het bijwerken van de winkelwagen is niet gelukt.'
      );

      buttons.forEach(button => {
        button.disabled = false;
      });
    } finally {
      cartActionRunning = false;
    }
  }

  /*
   * LIVE LINE PRICES
   */

  function linkedUnitPrice(item) {
    if (!priceCart || !item.quantity) {
      return item.final_price || 0;
    }

    let total = item.final_line_price;

    priceCart.items.forEach(service => {
      if (isService(service) && linksMatch(item, service)) {
        total += service.final_line_price;
      }
    });

    return total / item.quantity;
  }

  function money(cents) {
    return new Intl.NumberFormat(
      document.documentElement.lang || 'nl-NL',
      {
        style: 'currency',
        currency: priceCart?.currency || 'EUR'
      }
    ).format(cents / 100);
  }

  function renderPrices() {
    if (!priceCart || priceUpdating) return;

    priceUpdating = true;

    try {
      document.querySelectorAll(
        '[data-unbegun-main-cart] .cart-template__item[data-item-key]'
      ).forEach(row => {
        if (row.dataset.personalizationService === 'true') {
          return;
        }

        const item = priceCart.items.find(candidate =>
          candidate.key === row.dataset.itemKey &&
          !isService(candidate)
        );

        if (!item) return;

        const input = row.querySelector(
          '[data-unbegun-quantity]'
        );

        const output = row.querySelector(
          '.cart-template__line-price'
        );

        if (!input || !output) return;

        const amount = Number(input.value);

        if (!Number.isFinite(amount) || amount < 0) {
          return;
        }

        const expected = money(
          Math.round(linkedUnitPrice(item) * amount)
        );

        if (output.textContent.trim() !== expected) {
          output.textContent = expected;
        }
      });
    } finally {
      priceUpdating = false;
    }
  }

  function schedulePrices() {
    if (priceScheduled) return;

    priceScheduled = true;

    requestAnimationFrame(() => {
      priceScheduled = false;
      renderPrices();
    });
  }

  function initializePrices(cart) {
    const root = document.querySelector(
      '[data-unbegun-main-cart]'
    );

    if (!root) return;

    priceCart = cart;
    schedulePrices();

    const onInput = event => {
      if (event.target.matches(
        '[data-unbegun-main-cart] [data-unbegun-quantity]'
      )) {
        schedulePrices();
      }
    };

    document.addEventListener('input', onInput);
    document.addEventListener('change', onInput);

    const observer = new MutationObserver(() => {
      if (!priceUpdating) schedulePrices();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /*
   * QUICK CART
   */

  async function refreshDrawer() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      const root = drawer();
      if (!root) return false;

      const itemsRoot = root.querySelector('.js-items');
      const footerRoot = root.querySelector('.js-footer');

      try {
        const cart = await getCart();

        const visibleLines = cart.items.filter(
          item => !isService(item)
        ).length;

        let nextItems = null;

        for (let attempt = 0; attempt < 3; attempt++) {
          const response = await fetch(
            `${url('cart')}?section_id=quick-cart&_=${Date.now()}`,
            {
              credentials: 'same-origin',
              cache: 'no-store'
            }
          );

          if (response.ok) {
            const holder = document.createElement('div');
            holder.innerHTML = await response.text();

            const candidate = holder.querySelector(
              '[data-quick-cart-items]'
            );

            const rendered = candidate?.querySelectorAll(
              '[data-cart-item]'
            ).length || 0;

            if (candidate && (!visibleLines || rendered > 0)) {
              nextItems = candidate;
              break;
            }
          }

          if (attempt < 2) {
            await new Promise(resolve =>
              setTimeout(resolve, 250 * (attempt + 1))
            );
          }
        }

        const footerResponse = await fetch(
          `${url('cart')}?section_id=quick-cart-footer&_=${Date.now()}`,
          {
            credentials: 'same-origin',
            cache: 'no-store'
          }
        );

        if (nextItems && itemsRoot) {
          if (itemsRoot.innerHTML !== nextItems.innerHTML) {
            itemsRoot.innerHTML = nextItems.innerHTML;
          }
        }

        if (footerResponse.ok && footerRoot) {
          const holder = document.createElement('div');
          holder.innerHTML = await footerResponse.text();

          const footer = holder.querySelector(
            '[data-quick-cart-footer]'
          );

          const html = footer?.innerHTML || '';

          if (footerRoot.innerHTML !== html) {
            footerRoot.innerHTML = html;
          }
        }

        updateCount(cart);

        const title = root.querySelector('.quick-cart__title');

        if (title?.dataset.cartTitle) {
          title.textContent = title.dataset.cartTitle;
        }

        return true;
      } catch (error) {
        console.error(
          '[UNBEGUN Cart] Drawer refresh failed',
          error
        );

        return false;
      }
    })().finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  }

  async function reconcile() {
    if (reconcilePromise) return reconcilePromise;

    reconcilePromise = (async () => {
      for (let pass = 0; pass < 2; pass++) {
        let changed = false;

        if (window.UnbegunPersonalizer?.reconcile) {
          changed = Boolean(
            await window.UnbegunPersonalizer.reconcile()
          ) || changed;
        }

        if (window.UnbegunGiftWrap?.reconcile) {
          changed = Boolean(
            await window.UnbegunGiftWrap.reconcile()
          ) || changed;
        }

        if (window.UnbegunIpadKeyboard?.reconcile) {
          changed = Boolean(
            await window.UnbegunIpadKeyboard.reconcile()
          ) || changed;
        }

        if (!changed) break;
      }
    })().finally(() => {
      reconcilePromise = null;
    });

    return reconcilePromise;
  }

  async function reconcileAndRefresh() {
    await reconcile();
    await refreshDrawer();

    window.UnbegunPersonalizer?.lockServiceControls?.();
  }

  async function changeBaseQuantity(
    key,
    amount,
    { reload = false } = {}
  ) {
    if (cartActionRunning) return;

    cartActionRunning = true;

    try {
      const updatedCart = await synchronizeCart({
        [key]: quantity(amount)
      });

      if (reload) {
        rememberCount(updatedCart);
        window.location.reload();
      } else {
        await refreshDrawer();
      }
    } catch (error) {
      console.error(
        '[UNBEGUN Cart] Quantity update failed',
        error
      );

      alert('Het aantal kon niet worden bijgewerkt.');
    } finally {
      cartActionRunning = false;
    }
  }

  async function openDrawer() {
    const root = drawer();

    if (!root) {
      window.location.assign(url('cart'));
      return;
    }

    root.classList.add('is-active');

    document.documentElement.classList.add(
      'unbegun-cart-open'
    );

    await reconcileAndRefresh();

    requestAnimationFrame(() => {
      root.classList.add('is-visible');
    });
  }

  async function openAfterMutation() {
    try {
      await reconcile();

      sessionStorage.setItem(REOPEN_KEY, '1');
      window.location.reload();

      return true;
    } catch (error) {
      console.error('[UNBEGUN Cart] Reload failed', error);

      window.location.assign(url('cart'));
      return false;
    }
  }

  function reopenDrawer() {
    if (sessionStorage.getItem(REOPEN_KEY) !== '1') {
      return;
    }

    sessionStorage.removeItem(REOPEN_KEY);

    setTimeout(() => {
      const toggle = document.querySelector(
        '.js-cart-drawer-toggle'
      );

      if (toggle) toggle.click();
      else openDrawer();
    }, 180);
  }

  function closeDrawer() {
    const root = drawer();
    if (!root) return;

    root.classList.remove('is-visible');

    document.documentElement.classList.remove(
      'unbegun-cart-open'
    );

    setTimeout(() => {
      root.classList.remove('is-active');
    }, 300);
  }

  /*
   * CLICK EVENTS
   */

  document.addEventListener('click', async event => {
    if (event.target.closest(
      '[data-quick-cart] .js-tab-link-cart'
    )) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (event.target.closest('.js-cart-drawer-toggle')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openDrawer();
      return;
    }

    if (event.target.closest(
      '[data-quick-cart] .js-close, [data-quick-cart] .js-overlay'
    )) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeDrawer();
      return;
    }

    if (event.target.closest(
      '[data-quick-cart] .quick-cart__checkout-button'
    )) {
      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        await synchronizeCart();
        window.location.assign(url('checkout'));
      } catch (error) {
        console.error('[UNBEGUN Cart] Checkout failed', error);
        alert('De winkelwagen kon niet worden bijgewerkt.');
      }
      return;
    }

    if (event.target.closest(
      '[data-quick-cart] .quick-cart__view-cart-button'
    )) {
      event.preventDefault();
      event.stopImmediatePropagation();

      window.location.assign(url('cart'));
      return;
    }

    const arrow = event.target.closest(
      '[data-quick-cart] .js-unbegun-upsell-prev, ' +
      '[data-quick-cart] .js-unbegun-upsell-next'
    );

    if (arrow) {
      event.preventDefault();

      const track = arrow.closest(
        '.unbegun-cart-upsells'
      )?.querySelector('[data-unbegun-upsell-track]');

      if (track) {
        const direction = arrow.classList.contains(
          'js-unbegun-upsell-next'
        ) ? 1 : -1;

        track.scrollBy({
          left: direction * Math.max(
            155,
            track.clientWidth * 0.72
          ),
          behavior: 'smooth'
        });
      }
      return;
    }

    const quantityButton = event.target.closest(
      '[data-quick-cart] .js-add-single, ' +
      '[data-quick-cart] .js-remove-single'
    );

    if (quantityButton) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const row = quantityButton.closest('[data-cart-item]');

      if (!row || row.dataset.personalizationService === 'true') {
        return;
      }

      const key = row.dataset.itemKey;

      const current = Number(
        row.querySelector('.js-single-quantity')?.textContent || 0
      );

      const delta = quantityButton.classList.contains(
        'js-add-single'
      ) ? 1 : -1;

      await changeBaseQuantity(key, current + delta);
      return;
    }

    const remove = event.target.closest(
      '[data-unbegun-remove-item]'
    );

    if (remove) {
      event.preventDefault();
      event.stopImmediatePropagation();

      await changeBaseQuantity(
        remove.dataset.itemKey,
        0,
        { reload: true }
      );
      return;
    }

    const addButton = event.target.closest(
      '[data-add-button], button[name="add"], .product-form__submit'
    );

    if (addButton) {
      const productForm = addButton.closest(
        'form[data-product-form], form[action*="/cart/add"]'
      );

      const optionTransaction = Boolean(
        productForm?.querySelector(
          '[data-personalizer-toggle]:checked'
        ) ||
        productForm?.querySelector(
          '[data-gift-wrap-toggle]:checked'
        )
      );

      if (optionTransaction) return;

      setTimeout(async () => {
        await reconcileAndRefresh();

        const root = drawer();

        if (root?.classList.contains('is-active')) {
          root.classList.add('is-visible');
        }
      }, 650);
    }
  }, true);

  /*
   * CART FORM EVENTS
   */

  ['input', 'change'].forEach(type => {
    document.addEventListener(type, event => {
      const form = cartForm(event.target);

      if (!form || !event.target.matches(
        'input[name="updates[]"]'
      )) {
        return;
      }

      try {
        syncServiceInputs(form);
      } catch (error) {
        console.error(
          '[UNBEGUN Cart] Input sync failed',
          error
        );
      }
    }, true);
  });

  document.addEventListener('submit', event => {
    const form = cartForm(event.target);
    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (cartActionRunning) return;

    try {
      syncServiceInputs(form);

      updateCartPage(
        form,
        requestedQuantities(form),
        event.submitter?.name === 'checkout'
      );
    } catch (error) {
      console.error(
        '[UNBEGUN Cart] Form submit failed',
        error
      );

      alert('Controleer de aantallen in je winkelwagen.');
    }
  }, true);

  /*
   * UPSELLS
   */

  document.addEventListener('submit', async event => {
    const form = event.target.closest?.(
      '[data-unbegun-upsell-form]'
    );

    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (cartActionRunning) return;

    const button = form.querySelector('button[type="submit"]');

    cartActionRunning = true;
    button?.setAttribute('disabled', 'disabled');

    try {
      const response = await fetch(url('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description || 'Product toevoegen mislukt.'
        );
      }

      await reconcileAndRefresh();
    } catch (error) {
      console.error('[UNBEGUN Cart] Upsell failed', error);
    } finally {
      cartActionRunning = false;
      button?.removeAttribute('disabled');
    }
  }, true);

  /*
   * CART NOTES
   */

  document.addEventListener('input', event => {
    const note = event.target.closest?.(
      '[data-unbegun-cart-note]'
    );

    if (!note) return;

    const checkoutNote = drawer()?.querySelector(
      '[data-unbegun-checkout-note]'
    );

    if (checkoutNote) {
      checkoutNote.value = note.value;
    }

    const status = drawer()?.querySelector(
      '[data-unbegun-note-status]'
    );

    if (status) status.textContent = '';

    clearTimeout(noteSaveTimer);

    noteSaveTimer = setTimeout(async () => {
      try {
        await postUpdate({}, note.value);

        if (status) {
          status.textContent = '✓';

          setTimeout(() => {
            status.textContent = '';
          }, 1600);
        }
      } catch (error) {
        console.error(
          '[UNBEGUN Cart] Note save failed',
          error
        );
      }
    }, 450);
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeDrawer();
  });

  /*
   * INITIALIZATION
   */

  async function initialize() {
    drawer()?.querySelector(
      '.quick-cart__close'
    )?.setAttribute('type', 'button');

    /*
     * Restore the saved count before fetching the cart.
     */
    const restored = restoreCount();

    observeCount();

    try {
      const cart = await getCart();

      if (!restored) {
        updateCount(cart);
      }

      initializePrices(cart);

      /*
       * Verify the actual Shopify cart after the theme
       * has completed its initial rendering.
       */
      if (restored) {
        verifyCount();
      }
    } catch (error) {
      console.error(
        '[UNBEGUN Cart] Initialization failed',
        error
      );
    }

    reopenDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  window.UnbegunCart = {
    open: openDrawer,
    openAfterMutation,
    close: closeDrawer,
    refresh: refreshDrawer,
    reconcile: reconcileAndRefresh,
    changeQuantity: changeBaseQuantity,
    synchronize: synchronizeCart
  };
})();